export interface MessageParams {
  [key: string]: any;
}

export type ValidationResult = boolean | MessageParams;

export interface Validator {
  (value: any): ValidationResult | Promise<ValidationResult>;
}

export interface ValidatorMap {
  [key: string]: Validator;
}

export interface ValidationError {
  key: string;
  params?: MessageParams;
  expensive: boolean;
}

export async function validate(value: any, validators: ValidatorMap): Promise<Array<ValidationError>> {
  const errors: Array<ValidationError> = [];
  const promises = Object.entries(validators).map(async ([key, validator]) => {
    const rawResult = validator(value);
    const expensive = typeof rawResult === 'object' && 'then' in rawResult;
    const result = await rawResult;
    if (!result) {
      errors.push({ key, expensive });
    } else if (typeof result === 'object') {
      errors.push({ key, params: result, expensive });
    }
  });
  await Promise.all(promises);
  return errors;
}

export function dontCare(): boolean {
  return true;
}

export function required(value: any): boolean {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  } else if (Array.isArray(value)) {
    return value.length > 0;
  } else {
    return value !== null;
  }
}

export function numeric(value: any): boolean {
  const number = parseFloat(value);
  return !Number.isNaN(number) && Number.isFinite(number);
}

export function min(limit: number): Validator {
  return value => {
    if (typeof value === 'number' && value < limit) return { limit };
    return true;
  };
}

export function max(limit: number): Validator {
  return value => {
    if (typeof value === 'number' && value > limit) return { limit };
    return true;
  };
}

export function minLength(length: number): Validator {
  return value => {
    if (value && (typeof value === 'string' || Array.isArray(value)) && value.length < length) return { length };
    return true;
  };
}

export function maxLength(length: number): Validator {
  return value => {
    if ((typeof value === 'string' || Array.isArray(value)) && value.length > length) return { length };
    return true;
  };
}

const EMAIL_EXP = /^.+?@.+?(\..+?)+$/;

export function email(value: any): boolean {
  return typeof value !== 'string' || !value || EMAIL_EXP.test(value);
}

const ALPHA_NUM_EXP = /^[a-zA-Z0-9]*$/;

export function alphaNum(value: any): boolean {
  return !value || ALPHA_NUM_EXP.test(value);
}